import React, { useState } from "react";
import { Euro } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useLanguage } from "./LanguageContext";
import { LanguageSelector } from "./LanguageSelector";
import type { InvestorPage } from "@/store/slices/navigationSlice";
import { useUser } from "@/hooks/useUser";

type UserType = "investor";

interface LayoutProps {
  children: React.ReactNode;
  currentPage: InvestorPage;
  userType: UserType;
  onPageChange: (page: InvestorPage) => void;
  onLogout: () => void;
}

// Clean icon components - exactos como en la imagen
const BarChart3Icon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 3v18h18"></path>
    <path d="M18 17V9"></path>
    <path d="M13 17V5"></path>
    <path d="M8 17v-3"></path>
  </svg>
);

const TrendingUpIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22,7 13.5,15.5 8.5,10.5 2,17"></polyline>
    <polyline points="16,7 22,7 22,13"></polyline>
  </svg>
);

const BriefcaseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

// use lucide `Euro` icon for consistency with admin

/* const DollarSignIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
); */

/* const FileTextIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14,2 L6,2 C4.9,2 4,2.9 4,4 L4,20 C4,21.1 4.9,22 6,22 L18,22 C19.1,22 20,21.1 20,20 L20,8 L14,2 Z"></path>
    <polyline points="14,2 14,8 20,8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10,9 9,9 8,9"></polyline>
  </svg>
); */

const LogOutIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16,17 21,12 16,7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const MenuIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const XIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
);

const SettingsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

/* const GraduationCapIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"></path>
    <path d="M22 10v6"></path>
    <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"></path>
  </svg>
); */

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Clean Button component
const Button = React.forwardRef<
  HTMLButtonElement,
  {
    variant?: "ghost" | "secondary" | "outline" | "default";
    className?: string;
    onClick?: () => void;
    children: React.ReactNode;
    disabled?: boolean;
    size?: "icon" | "sm" | "default";
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(
  (
    {
      variant = "default",
      size = "default",
      className = "",
      onClick,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-start rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

    const variants = {
      default: "bg-primary text-primary-foreground hover:bg-primary/90",
      ghost: "hover:bg-gray-100 hover:text-gray-900",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      outline:
        "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    };

    const sizes = {
      default: "h-10 px-4 py-2 text-sm font-medium",
      sm: "h-9 rounded-md px-3 text-sm",
      icon: "h-10 w-10",
    };

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export function Layout({
  children,
  currentPage,
  onPageChange,
  onLogout,
}: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const { t } = useLanguage();
  const { user, profile } = useUser();
  // Build a friendly display name:
  // 1) prefer profile.full_name, profile.first_name + last_name or profile.name
  // 2) then user.first_name + user.last_name or user.full_name
  // 3) finally, format the email local-part into a readable name (john.doe -> John Doe)
  const formatNameFromEmail = (email?: string) => {
    if (!email) return null;
    const local = email.split("@")[0] || "";
    const parts = local.split(/[._-]+/).filter(Boolean);
    if (parts.length === 0) return null;
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
  };

  const getNameFromEntity = (ent: any) => {
    if (!ent) return null;
    if (typeof ent.full_name === "string" && ent.full_name.trim().length > 0)
      return ent.full_name.trim();
    if (typeof ent.name === "string" && ent.name.trim().length > 0) return ent.name.trim();
    const first = ent.first_name ?? ent.firstName ?? "";
    const last = ent.last_name ?? ent.lastName ?? "";
    const combined = `${first} ${last}`.trim();
    return combined.length > 0 ? combined : null;
  };

  const rawName =
    getNameFromEntity(profile) || getNameFromEntity(user) || formatNameFromEmail(user?.email) || "Usuario";
  const userName = typeof rawName === "string" && rawName.length > 0 ? rawName : "Usuario";

  type NavigationItem = {
    key: string;
    label: string;
    icon: React.ComponentType<any>;
    isActive?: boolean;
    hasSubmenu?: boolean;
    isOpen?: boolean;
    subItems?: { key: string; label: string; url?: string }[];
  };

  const navigationItems: NavigationItem[] = [
    {
      key: "dashboard",
      label: t("nav.dashboard"),
      icon: BarChart3Icon,
      isActive: currentPage === "dashboard",
    },
    {
      key: "mi-inversion",
      label: t("nav.miInversion"),
      icon: TrendingUpIcon,
      isActive: currentPage === "mi-inversion",
    },
    {
      key: "portfolio",
      label: t("nav.portfolio"),
      icon: BriefcaseIcon,
      isActive: currentPage === "portfolio",
    },
    {
      key: "reportes",
      label: t("nav.reportes"),
      icon: BarChart3Icon,
      isActive: currentPage.startsWith("reportes"),
      hasSubmenu: true,
      isOpen: reportsOpen,
      subItems: [
        { key: "reportes-financiero", label: "nav.reportes.financiero", url: "https://drive.google.com/drive/folders/1hVcqZBGNkeMajsqBJFtwiYY8OXiy8gWQ?usp=sharing" },
        { key: "reportes-operativo", label: "nav.reportes.operativo", url: "https://drive.google.com/drive/folders/1e4n5jo896nHofnZaJJj15-05NPx8DY0y?usp=sharing" },
        { key: "reportes-documentacion", label: "nav.reportes.documentacion", url: "https://drive.google.com/drive/folders/1kf8WpUt7y8jJx-udzUnkfZ4VoqCiiNR8?usp=sharing" },
        { key: "fees", label: "nav.fees" },
      ],
    },
    /* {
      key: "pro-ratas",
      label: t("nav.deals"),
      icon: DollarSignIcon,
      isActive: currentPage === "pro-ratas"
    }, */
    /*  {
      key: "reportes",
      label: t("nav.reportes"),
      icon: FileTextIcon,
      isActive: currentPage.startsWith("reportes"),
      hasSubmenu: true,
      isOpen: reportsOpen,
      subItems: [
        { key: "reportes-financiero", label: t("nav.reportes.financiero") },
        { key: "reportes-operativo", label: t("nav.reportes.operativo") },
        {
          key: "reportes-documentacion",
          label: t("nav.reportes.documentacion"),
        },
      ],
    }, */
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-premium">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Left side - Menu button and logo */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden rounded-lg hover:bg-gray-50"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <XIcon /> : <MenuIcon />}
            </Button>

            {/* Logo section (clickable -> dashboard) */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  onPageChange("dashboard");
                  setSidebarOpen(false);
                }}
                className="text-left text-lg font-semibold text-gray-900 bg-transparent p-0 m-0 cursor-pointer"
                aria-label="Ir al dashboard"
              >
                Bellamy & Partners
              </button>
            </div>
          </div>

          {/* Right side - Language selector and User actions */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <LanguageSelector />

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 h-9 px-3 rounded-lg hover:bg-gray-50 text-gray-700"
                >
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-medium">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">
                    {userName}
                  </span>
                  <ChevronDownIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => onPageChange("profile")}
                  className="flex items-center gap-2"
                >
                  <UserIcon />
                  {t("user.profile")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onLogout}
                  className="flex items-center gap-2 text-red-600 focus:text-red-600"
                >
                  <LogOutIcon />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-60 transform border-r border-gray-200 bg-white transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="flex flex-col h-full">
          {/* User Info Section - Clickeable */}
          <div className="flex-shrink-0 p-4 border-b border-gray-100">
            <Button
              variant="ghost"
              className="w-full p-0 h-auto justify-start hover:bg-gray-50 rounded-lg transition-all duration-150"
              onClick={() => {
                onPageChange("profile");
                setSidebarOpen(false);
              }}
            >
              <div className="flex items-center gap-3 w-full p-2">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {userName}
                  </p>
                  <p className="text-xs text-gray-500">{t("user.role")}</p>
                </div>
              </div>
            </Button>
          </div>

          {/* Main Navigation - Scrollable */}
          <div className="flex-1 overflow-y-auto py-4 min-h-0">
            <div className="space-y-1 px-3">
              {navigationItems.map((item) => (
                <div key={item.key}>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start gap-3 h-10 text-sm font-normal relative rounded-xl transition-all duration-150 ${
                      item.isActive
                        ? "bg-gray-100 text-gray-900 shadow-sm"
                        : "hover:bg-gray-50 text-gray-600 hover:text-gray-900"
                    } ${item.hasSubmenu ? "cursor-pointer" : ""}`}
                    onClick={() => {
                      if (item.hasSubmenu) {
                        setReportsOpen(!reportsOpen);
                      } else {
                        onPageChange(item.key as InvestorPage);
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    <item.icon />
                    {item.label}
                    {item.key === "pro-ratas" && (
                      <div className="ml-auto w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          1
                        </span>
                      </div>
                    )}
                    {item.hasSubmenu && (
                      <ChevronRightIcon
                        className={`ml-auto transition-all duration-200 ease-in-out text-[#666] hover:text-black ${
                          item.isOpen ? "rotate-90" : "rotate-0"
                        }`}
                      />
                    )}
                  </Button>

                  {/* Submenu for Reports - Cascading Animation */}
                  {item.hasSubmenu && (
                    <div className="overflow-hidden">
                      <div
                        className={`ml-6 mt-2 bg-white rounded-lg transition-all duration-200 ease-in-out ${
                          reportsOpen
                            ? "max-h-96 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="py-2 space-y-2">
                          {item.subItems?.map((subItem, index) => (
                            <Button
                              key={subItem.key}
                              variant="ghost"
                              className={`w-full justify-start h-9 text-xs font-normal transition-all duration-180 ease-out transform ${
                                reportsOpen
                                  ? "translate-y-0 opacity-100"
                                  : "-translate-y-2 opacity-0"
                              } ${
                                currentPage === subItem.key
                                  ? "bg-gray-100 text-black"
                                  : "hover:bg-[#F5F6FA] text-gray-600 hover:text-black"
                              } ${index === 0 ? 'rounded-full px-4 py-2 text-sm bg-gray-100/90' : 'rounded-lg px-2'}`}
                              style={{
                                transitionDelay: reportsOpen
                                  ? `${index * 100}ms`
                                  : "0ms",
                              }}
                              onClick={() => {
                                // If subItem has external URL, open in new tab
                                if (subItem.url) {
                                  window.open(subItem.url, '_blank', 'noopener,noreferrer');
                                  setSidebarOpen(false);
                                  return;
                                }
                                onPageChange(subItem.key as InvestorPage);
                                setSidebarOpen(false);
                              }}
                              >
                                {typeof subItem.label === 'string' && subItem.label.startsWith('nav.')
                                  ? t(subItem.label)
                                  : subItem.label}
                              </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom items */}
          <div className="flex-shrink-0 p-3 border-t border-gray-100 space-y-1">
            <Button
              variant="ghost"
              className={`w-full justify-start gap-3 h-10 text-sm font-normal rounded-lg transition-all duration-150 ${
                currentPage === "profile"
                  ? "bg-gray-100 text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              onClick={() => {
                onPageChange("profile");
                setSidebarOpen(false);
              }}
            >
              <SettingsIcon />
              {t("nav.profile")}
            </Button>

            {/* <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-10 text-sm font-normal text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-150"
              onClick={() => {
                onPageChange("academy");
                setSidebarOpen(false);
              }}
            >
              <GraduationCapIcon />
              {t("nav.academy")}
            </Button> */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-10 text-sm font-normal text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-150"
              onClick={onLogout}
            >
              <LogOutIcon />
              {t("nav.logout")}
            </Button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-60 pt-16">
        <div className="min-h-screen bg-gray-50">{children}</div>
      </main>
    </div>
  );
}
